import { formatNOK } from '@polf/shared/locale'

import { IconPokerChip } from '../../../components/Icon'
import { payoutAmountsNOK } from '../../../util/payouts'

/**
 * Payout ladder. NOK amounts when prize pool is set (round-down-100 +
 * residual to 1st, mirrors PayoutEditor on the floor view). Percentage
 * fallback otherwise.
 *
 * `highlightPosition` paints one row gold-bordered — used to mark the
 * "your finish if it ended now" row from `payJump.positionIfNow`. Drives
 * the visual chain: SeatCard says "5. plass sikret", PayoutList rings
 * row 5 to match.
 */
const PayoutList = ({
  payouts,
  prizePool,
  highlightPosition,
}: {
  payouts: ReadonlyArray<{ position: number; percentageBp: number }>
  prizePool: number | null | undefined
  highlightPosition?: number | null
}) => {
  const nokByPosition =
    prizePool && prizePool > 0 ? payoutAmountsNOK(payouts, prizePool) : null
  const sorted = [...payouts].sort((a, b) => a.position - b.position)
  return (
    <div className="mx-2 my-3 bg-paper rounded-lg shadow overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-ink-100">
        <span className="up text-ink-500 flex items-center gap-2">
          <IconPokerChip size={14} className="text-gold" />
          Premier
        </span>
        {nokByPosition ? (
          <span className="text-xs text-ink-500 num">
            Pot {formatNOK(prizePool!)}
          </span>
        ) : (
          <span className="text-xs text-ink-500 num">Top {payouts.length}</span>
        )}
      </div>
      {sorted.map((p) => {
        let tone = 'bg-cream-card text-ink-700'
        if (p.position === 1) tone = 'bg-gold text-paper'
        else if (p.position === 2) tone = 'bg-ink-300 text-ink-900'
        else if (p.position === 3) tone = 'bg-flag-dark text-paper'

        const kr = nokByPosition?.get(p.position) ?? null
        const highlight = highlightPosition === p.position
        return (
          <div
            key={p.position}
            className={`flex items-center gap-3 px-4 py-2 border-b border-ink-100 last:border-b-0 ${
              highlight ? 'bg-gold/10' : ''
            }`}
          >
            <div
              className={`w-7 h-7 rounded flex items-center justify-center font-bold font-display ${tone}`}
            >
              {p.position}
            </div>
            <div className="flex-1 text-sm font-semibold text-ink-900 flex items-center gap-2">
              <span>{p.position}. plass</span>
              {highlight ? (
                <span className="text-[10px] font-bold uppercase tracking-wider text-gold bg-gold/15 px-1.5 py-0.5 rounded-full">
                  Du
                </span>
              ) : null}
            </div>
            <div className="font-mono num font-bold text-ink-900">
              {kr != null
                ? formatNOK(kr)
                : `${(p.percentageBp / 100).toFixed(1).replace('.', ',')}%`}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default PayoutList
